import PushPin from '@mui/icons-material/PushPin'
import { Box } from '@mui/material'
import { useTranslations } from 'next-intl'
import { usePathname } from 'next/navigation'
import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import type { NavGroup } from '@core/layouts/types'

import { useSettings } from '@core/context/settingsContext'

import flattenNavItems from '~/components/QuickSearch/utils/flattenNavItems'
import { useStatsigGate } from '~/configs/statsig'
import { useCurrentUser } from '~/data/api/hooks/users'
import usePinnedNavItems from '~/hooks/usePinnedNavItems'
import { AbilityContext } from '~/layouts/components/acl/Can'
import routeDirectory from '~/navigation/routes'
import useNavItems from '~/navigation/vertical'

import NavThemeProvider from '@core/layouts/components/NavThemeProvider'

import Drawer from './Drawer'
import VerticalNavGroup, { loadPersistedOpenState, persistOpenState } from './VerticalNavGroup'
import VerticalNavHeader from './VerticalNavHeader'
import findClosedGroupForPath from './autoExpand'

/**
 * Desktop sidebar intentionally does NOT expand on hover. Users toggle
 * collapse/expand via the chevron button in `VerticalNavHeader`.
 */

type Props = {
  navWidth: number
  navVisible: boolean
  collapsedNavWidth: number
  hidden: boolean
  navigationBorderWidth: number
  toggleNavVisibility: () => void
  setNavVisible: (value: boolean) => void
}

const PINNED_GROUP_KEY = 'pinned'

// Curated list of high-value destinations seeded as pins for first-load
// users. The goal is two-fold: (1) make the pinning feature discoverable
// and (2) keep the most-used screens above the fold for the typical user.
// Paths that don't resolve in a given org (e.g. ChargeAI on an org without
// the gate) silently drop out of the rendered Pinned group via the existing
// resolver in `pinnedGroup` below — the orphan entry sits harmlessly in
// localStorage in case the org gets the feature later.
const DEFAULT_PINNED_PATHS: string[] = [
  routeDirectory.dashboard.root,
  routeDirectory.chargers.root,
  routeDirectory.vehicles.root,
  routeDirectory.chargeai.root
]

/**
 * Derive each group's default open/closed state from its `defaultOpen` field
 * (set in `useNavItems`). Groups without `defaultOpen` default to closed. The
 * synthetic Pinned group is always open by default.
 */
function deriveDefaultOpenGroups(groups: NavGroup[]): Record<string, boolean> {
  const defaults: Record<string, boolean> = { [PINNED_GROUP_KEY]: true }
  for (const group of groups) {
    defaults[group.key] = group.defaultOpen ?? false
  }
  return defaults
}

/**
 * Matches the drawer paper's `width .25s ease-in-out` transition. When
 * collapsing we keep the body in its expanded layout until the drawer has
 * visually shrunk, so the labels don't snap to icons while the rail is still
 * wide. When expanding we flip immediately so the content that slides in is
 * already in its final layout.
 */
const DRAWER_TRANSITION_MS = 250

const Navigation = (props: Props) => {
  const { hidden, navVisible, setNavVisible } = props
  const { settings } = useSettings()
  const navItems = useNavItems()
  const { synopUser } = useCurrentUser()
  const userId = synopUser?.id
  const t = useTranslations('layouts.navDrawer')
  const ability = useContext(AbilityContext)
  const { pins, reorder, seedPinsIfMissing } = usePinnedNavItems()
  // Reserve room at the bottom of the scrollable nav so the full-width assistant
  // bar (rendered above the drawer) doesn't cover the last nav items. Only when
  // the bar is actually shown — otherwise the sidebar keeps its compact footer.
  const { enabled: assistantBarEnabled } = useStatsigGate('ai-assistant')

  // Seed default pins exactly once per user — only when the user has never
  // touched pins. `useLayoutEffect` (rather than `useEffect`) runs between
  // commit and paint, so on first ever load the seeded Pinned group lands
  // in the DOM before the browser paints — no visible empty-sidebar →
  // populated-sidebar flicker. Subsequent renders are no-ops because
  // `seedPinsIfMissing` short-circuits when the storage key is present.
  // Navigation only mounts after the `nav-redesign` Statsig gate resolves
  // on the client, so SSR concerns around `useLayoutEffect` don't apply.
  useLayoutEffect(() => {
    if (!userId) return
    seedPinsIfMissing(DEFAULT_PINNED_PATHS)
  }, [userId, seedPinsIfMissing])

  const flattened = useMemo(() => flattenNavItems(navItems), [navItems])

  // Build the synthetic Pinned group from the user's pin list (in array
  // order, which is also the canonical reorderable order).
  const pinnedGroup = useMemo<NavGroup | null>(() => {
    if (pins.length === 0) return null

    const items = pins
      .map((pin) => flattened.find((link) => link.path === pin.path))
      .filter((link): link is (typeof flattened)[number] => Boolean(link))
      .filter((link) => {
        if (link.auth === false) return true
        return ability?.can(link.action, link.subject) ?? false
      })

    if (items.length === 0) return null

    return {
      key: PINNED_GROUP_KEY,
      title: t('pinned'),
      icon: <PushPin />,
      items
    }
  }, [pins, flattened, ability, t])

  // Hide pinned items from their original menu locations so they only render
  // in the synthetic Pinned group at the top — avoids each pinned link
  // appearing twice in the sidebar.
  const filteredNavItems = useMemo(() => {
    if (pins.length === 0) return navItems
    const pinnedPaths = new Set(pins.map((p) => p.path))
    return navItems.map((group) => ({
      ...group,
      items: group.items.filter((item) => !item.path || !pinnedPaths.has(item.path))
    }))
  }, [navItems, pins])

  const groups = useMemo(
    () => (pinnedGroup ? [pinnedGroup, ...filteredNavItems] : filteredNavItems),
    [pinnedGroup, filteredNavItems]
  )

  const defaultOpenGroups = useMemo(() => deriveDefaultOpenGroups(navItems), [navItems])

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(defaultOpenGroups)
  useEffect(() => {
    // Always reset to defaults on user change so a same-tab account switch
    // doesn't inherit the previous user's open/closed groups; then overlay
    // any persisted prefs the new user has.
    const persisted = loadPersistedOpenState(userId)
    setOpenGroups({ ...defaultOpenGroups, ...persisted })
  }, [userId, defaultOpenGroups])

  const toggleGroup = useCallback(
    (key: string) => {
      setOpenGroups((prev) => {
        const next = { ...prev, [key]: !prev[key] }
        persistOpenState(next, userId)
        return next
      })
    },
    [userId]
  )

  // Auto-expand the parent section when the user lands on a page via Cmd+K
  // (Quick Search), a `G`-leader keyboard shortcut, an address-bar paste, or
  // any other route change. Mouse clicks on a nav item already open the section
  // as a side-effect — this catches the "skipped the nav UI" navigation paths
  // so users land oriented (visual breadcrumb of where they are).
  //
  // Strictly additive (only opens closed groups, never closes open ones) and
  // in-memory only (does NOT call `persistOpenState`) so contextual auto-expand
  // doesn't overwrite the user's explicit collapse preferences across sessions.
  // Pure helper extracted to `./autoExpand` for unit-test coverage.
  //
  // `groups` is captured via a ref so the effect does NOT re-fire on structural
  // nav recomputes — `groups` gets a new reference whenever the user pins/unpins
  // or when `navItems`/`ability`/`t` change. Without the ref, a manual collapse
  // of the current section would be undone the next time `groups` recomputes
  // (see Cursor Bugbot finding on PR #2256). `userId` IS kept in the dep list
  // so a same-tab org switch still re-evaluates auto-expand for the new user's
  // nav structure — pin/unpin doesn't trigger it, but org switch should.
  const pathname = usePathname()
  const groupsRef = useRef(groups)
  useEffect(() => {
    groupsRef.current = groups
  }, [groups])
  useEffect(() => {
    setOpenGroups((prev) => {
      const groupKey = findClosedGroupForPath(groupsRef.current, prev, pathname)
      if (!groupKey) return prev
      return { ...prev, [groupKey]: true }
    })
  }, [pathname, userId])

  // After auto-expand opens the parent section, scroll the active item into
  // view if it's below (or above) the visible nav viewport. Without this,
  // landing on a page in a section near the bottom of the nav (e.g. Settings)
  // via Cmd+K or `G ,` leaves the freshly-expanded section invisible until
  // the user manually scrolls. Per user testing report on PR #2256 (R3.1).
  //
  // `block: 'nearest'` makes this a no-op when the active row is already in
  // view — mouse-click navigations don't trigger unnecessary scrolling.
  // `requestAnimationFrame` defers the lookup to the next paint so the
  // `setOpenGroups` state update above has flushed and the active row's
  // final position is known; without rAF the query can miss a row that
  // just appeared via the expansion.
  useEffect(() => {
    if (!pathname || typeof window === 'undefined') return
    const handle = window.requestAnimationFrame(() => {
      // The active row is marked with `aria-current="page"` by
      // `VerticalNavChildRow`. Querying within the sidebar's `<nav>` element
      // keeps the lookup scoped — unrelated `aria-current` markers elsewhere
      // in the app (page tabs, breadcrumbs, etc.) won't be picked up.
      const activeRow = document.querySelector<HTMLElement>('nav [aria-current="page"]')
      activeRow?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    })
    return () => window.cancelAnimationFrame(handle)
  }, [pathname, userId])

  // The Pinned group's `items` are the user's pins after dropping any that
  // are unresolvable (feature flag off) or fail an ability check, so the
  // visible-list indices the child rows emit can diverge from indices into
  // the canonical pins array. Translate visible→storage here so the storage
  // mutation lands on the correct entry. Without this, dragging a row would
  // silently move a different (filtered-out) pin in localStorage.
  const handlePinnedReorder = useCallback(
    (fromVisibleIndex: number, toVisibleIndex: number) => {
      const visibleItems = pinnedGroup?.items
      if (!visibleItems) return
      const fromPath = visibleItems[fromVisibleIndex]?.path
      const toPath = visibleItems[toVisibleIndex]?.path
      if (!fromPath || !toPath) return
      const fromStorage = pins.findIndex((p) => p.path === fromPath)
      const toStorage = pins.findIndex((p) => p.path === toPath)
      if (fromStorage === -1 || toStorage === -1) return
      reorder(fromStorage, toStorage)
    },
    [pinnedGroup, pins, reorder]
  )

  const closeMobileDrawer = useCallback(() => {
    if (navVisible) setNavVisible(false)
  }, [navVisible, setNavVisible])

  // Mobile (temporary) drawer is always expanded when visible; collapsed only applies to desktop.
  const collapsed = hidden ? false : settings.navCollapsed

  // Body layout (labels vs icon-only) follows `collapsed` immediately when
  // expanding and lags by the drawer's transition duration when collapsing.
  const [bodyCollapsed, setBodyCollapsed] = useState(collapsed)
  useEffect(() => {
    if (!collapsed) {
      setBodyCollapsed(false)
      return
    }
    const timeout = window.setTimeout(() => setBodyCollapsed(true), DRAWER_TRANSITION_MS)
    return () => window.clearTimeout(timeout)
  }, [collapsed])

  return (
    <NavThemeProvider>
      <Drawer {...props}>
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <VerticalNavHeader {...props} />
          <Box
            component="nav"
            aria-label={t('mainNav')}
            sx={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              px: 1,
              pt: 2,
              // The bar grows (suggestion chips, multi-line input) and publishes
              // its live height; pad by that, not a guess, so the last item is
              // never underneath it.
              pb: assistantBarEnabled ? 'calc(var(--synop-assistant-bar-height, 49px) + 8px)' : 1,
              scrollbarWidth: 'thin',
              scrollbarColor: (theme) => `${theme.palette.mode === 'light' ? '#BFBFD5' : '#4A4A4A'} transparent`,
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: (theme) => (theme.palette.mode === 'light' ? '#BFBFD5' : '#4A4A4A'),
                borderRadius: 4
              },
              '&:hover::-webkit-scrollbar': { width: 6 }
            }}
          >
            {groups.map((group) => (
              <VerticalNavGroup
                key={group.key}
                group={group}
                collapsed={bodyCollapsed}
                open={openGroups[group.key] ?? false}
                onToggle={() => toggleGroup(group.key)}
                onNavigate={closeMobileDrawer}
                reorderable={group.key === PINNED_GROUP_KEY}
                onReorder={group.key === PINNED_GROUP_KEY ? handlePinnedReorder : undefined}
              />
            ))}
          </Box>
        </Box>
      </Drawer>
    </NavThemeProvider>
  )
}

export default Navigation
